/**
 * Lightweight SEO helper: set <title>, meta description, OG tags, canonical, and JSON-LD
 * without adding a heavy dependency. Cleans up on unmount.
 */
import { useEffect } from "react";

const BASE_TITLE = "CV. Komunika Teknik";
const DEFAULT_DESCRIPTION =
  "Penyedia layanan teknologi informasi, pengembangan sistem informasi, dan pengadaan perangkat TI untuk pemerintah, BUMN, dan korporasi di Indonesia.";
const DEFAULT_OG_IMAGE =
  "https://customer-assets.emergentagent.com/job_komunika-infotech/artifacts/5hulk6sk_logo.png";

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function useSeo({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_OG_IMAGE,
  jsonLd,
  path,
} = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} · ${BASE_TITLE}` : `${BASE_TITLE} · IT Solutions`;
    const url =
      typeof window !== "undefined"
        ? window.location.origin + (path || window.location.pathname)
        : "";

    document.title = fullTitle;
    setMeta("description", description);

    setMeta("og:type", "website", "property");
    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", description, "property");
    setMeta("og:image", image, "property");
    setMeta("og:url", url, "property");
    setMeta("og:site_name", BASE_TITLE, "property");

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", description);
    setMeta("twitter:image", image);

    setLink("canonical", url);

    if (jsonLd) {
      setJsonLd("kt-jsonld", jsonLd);
    }
  }, [title, description, image, JSON.stringify(jsonLd), path]);
}

export const ORG_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "CV. Komunika Teknik",
  url: typeof window !== "undefined" ? window.location.origin : "",
  logo: DEFAULT_OG_IMAGE,
  description: DEFAULT_DESCRIPTION,
  address: {
    "@type": "PostalAddress",
    streetAddress: "Jl. Yos Sudarso RT. 16 No. 28, Selumit, Tarakan Tengah",
    addressLocality: "Tarakan",
    addressRegion: "Kalimantan Utara",
    addressCountry: "ID",
  },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    email: "info@komunikateknik.online",
    telephone: "+62-852-4628-6550",
    areaServed: "ID",
    availableLanguage: ["id", "en"],
  },
};
